import React from "react";
import { useQueryClient } from "react-query";
import { useLogoutOnAccessDenied } from "shared/hooks/useLogoutOnAccessDenied";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { SESSION_EXPIRE_TOAST_ID } from "shared/utils/constants";
import { AppType, LocalStorageKey } from "shared/utils/enums";
import LocalStorage from "shared/utils/storage/LocalStorage";
import Tokens from "shared/utils/storage/tokens";
import { dismissToast } from "shared/utils/toastNotification";
import Reducer from "./reducer";
import { AuthContextType, AuthState, AuthDispatchInterface } from "./type.d";
import LanguageType from "../../enums/LanguageType";
import { useStaticData } from "../../../context/staticData";
import { getLocaleId, setLocaleId } from "shared/utils";
import { ApiLoggedInUser } from "context/authority/types";
import { getEnvironmentInfo } from "shared/utils/config";
import { useLogout } from "shared/components/login/dataloader";

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

/**
 * Console Auth Context Provider component
 */

const token = Tokens.getInstance();
function AuthProvider(props: React.PropsWithChildren<{}>) {
  const [state, dispatch] = useReducerWrapper<AuthState, AuthDispatchInterface>(Reducer);
  const { languageTypeLocaleId } = useStaticData();
  const queryClient = useQueryClient();
  const { mutateAsync: logoutUser } = useLogout();

  const postLoginChores = React.useCallback(
    (userDetail: ApiLoggedInUser) => {
      //Set Locale id in admin console after login. In reg and myExp using reg detail api to set locale.
      const isAdminConsole = getEnvironmentInfo().appType === AppType.CONSOLE;
      if (isAdminConsole) {
        setLocaleId(languageTypeLocaleId[userDetail?.languageType]);
      }
    },
    [languageTypeLocaleId]
  );

  const login = React.useCallback(
    (data: any): void => {
      token.setAccessToken(data.accessToken);
      dispatch.loginAction(data.accessToken);
      dispatch.saveUserDetailAction(data);
      LocalStorage.setItem(LocalStorageKey.USER_DETAIL, JSON.stringify(data));
      //dismiss session expired error toast on login
      dismissToast(SESSION_EXPIRE_TOAST_ID);
      postLoginChores(data);
    },
    [dispatch, postLoginChores]
  );

  React.useEffect(() => {
    function handleLoginUpdateFromOtherTabs(event: StorageEvent) {
      if (event.key === LocalStorageKey.TOKEN && event.oldValue && event.oldValue !== event.newValue) {
        window.location.href = "/";
      }
    }
    window.addEventListener("storage", handleLoginUpdateFromOtherTabs);
    return () => window.removeEventListener("storage", handleLoginUpdateFromOtherTabs);
  }, []);

  const isLoggedIn = React.useCallback((): boolean => {
    return Boolean(state.token);
  }, [state.token]);

  const logout = React.useCallback(
    async (callback = () => {}): Promise<void> => {
      if (isLoggedIn()) {
        try {
          await logoutUser({});
        } catch (err) {}
        callback();
        token.setAccessToken("");
        dispatch.logoutAction();
        dispatch.saveUserDetailAction();
        localStorage.clear();
        queryClient.clear();
        sessionStorage.clear();
        LocalStorage.removeItem(LocalStorageKey.USER_DETAIL);
      } else {
        callback();
      }
      callback();
    },
    [dispatch, isLoggedIn, logoutUser, queryClient]
  );

  const getSelectedLanguage = React.useCallback(() => {
    const localeId = getLocaleId();
    const languageTypeEnum = Object.keys(languageTypeLocaleId).find(key => languageTypeLocaleId[key] === localeId);
    return (languageTypeEnum || LanguageType.ENGLISH) as LanguageType;
  }, [languageTypeLocaleId]);

  const interceptorInitialized = useLogoutOnAccessDenied(logout);

  if (!interceptorInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        userDetail: state.userDetail,
        getSelectedLanguage
      }}
      {...props}
    />
  );
}

/**
 * Returns Console Auth Context
 */
function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthProvider, useAuth };
